import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { useAgentConfig } from '../contexts/AgentConfigContext';
import ThemeSection from './ThemeSection';
import SecurityParamsSection from './SecurityParamsSection';
import MemorySection from './MemorySection';

/**
 * The app's one Settings surface: the security parameters that ride along with
 * every question, what agents remember about you, and appearance. Opened by the
 * navbar's gear button.
 *
 * Three tabs rather than one scrolling column, ordered by how much each changes
 * an answer. Security comes first because a wrong parameter returns the wrong
 * rows; Memory next because a stored preference shapes every reply; Theme last
 * because it only changes how the reply looks — and its grid is tall enough that
 * it used to push the parameters below the fold.
 *
 * Applying closes the modal — the parameters are stored per agent until
 * logout, so they are set once rather than tuned repeatedly.
 *
 * Rendered through a portal on document.body. Its caller is the navbar, whose
 * fixed z-2050 shell is a stacking context: nested inside it, this dialog's own
 * z-index would be capped there and the sidebar would paint over it.
 */

const TITLE_ID = 'settings-modal-title';

const SECTION_HEADING_CLASS = 'text-xs font-semibold uppercase tracking-wide text-fg-muted mb-3';

const TABS = [
  { id: 'security', label: 'Security' },
  // Between Security and Theme on the same ordering argument the two of them
  // already encode: a memory changes what an answer says, a theme only how it
  // looks. It sits after Security because a wrong parameter returns wrong rows,
  // which is the more immediate failure.
  { id: 'memory', label: 'Memory' },
  { id: 'theme', label: 'Theme' },
];

export default function SettingsModal({ open, onClose }) {
  const { securityParams, setSecurityParams } = useAgentConfig();
  const dialogRef = useRef(null);
  const [activeTab, setActiveTab] = useState(TABS[0].id);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Land keyboard focus inside the dialog. On close, focus goes
  // back where it came from — otherwise it restarts at the top of the document
  // and the gear button is lost.
  useEffect(() => {
    if (!open) {
      // Reopen on Security rather than wherever the last visit ended: it is the
      // tab with consequences, so it should not be possible to miss it.
      setActiveTab(TABS[0].id);
      return undefined;
    }
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  // aria-modal tells assistive tech the rest of the page is inert; it does not
  // stop Tab from walking out of the dialog into the page behind it.
  const handleTabKey = useCallback((event) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  // Left/Right move between tabs, which is what a tablist is expected to do.
  // Both tabs stay tabbable rather than using a roving tabindex: the dialog's
  // own Tab trap above walks focusable elements, and taking one out of that set
  // would change where the trap wraps.
  const handleTabArrows = useCallback((event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = TABS.findIndex(({ id }) => id === activeTab);
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = TABS[(index + delta + TABS.length) % TABS.length];
    setActiveTab(next.id);
    document.getElementById(`settings-tab-${next.id}`)?.focus();
  }, [activeTab]);

  useEffect(() => {
    if (!open) return undefined;
    // Restore whatever was there rather than clearing to '': another overlay
    // (a document preview) may have locked scrolling first and still need it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const handleApply = (params) => {
    setSecurityParams(params);
    onClose();
  };

  const handleClear = () => setSecurityParams({});

  if (!open) return null;

  return createPortal(
    <div
      // Dismiss on the press, not the click: a click is delivered to the
      // common ancestor of press and release, so selecting text in a field and
      // releasing past the dialog edge would otherwise discard unapplied rows.
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        onKeyDown={handleTabKey}
        className="animate-rise flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-line bg-surface"
        style={{ boxShadow: 'var(--shadow-float)' }}
      >
        <div className="flex flex-none items-center justify-between border-b border-line px-6 py-4">
          <h2 id={TITLE_ID} className="text-lg font-semibold text-fg">
            Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="grid h-8 w-8 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg focus-visible:ring-2 focus-visible:ring-brand-soft"
            style={{
              transitionDuration: 'var(--d-fast)',
              transitionTimingFunction: 'var(--ease-out)',
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div role="tablist" aria-label="Settings sections" className="flex flex-none gap-1 border-b border-line px-6">
          {TABS.map(({ id, label }) => {
            const selected = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`settings-tab-${id}`}
                aria-selected={selected}
                aria-controls={`settings-panel-${id}`}
                onClick={() => setActiveTab(id)}
                onKeyDown={handleTabArrows}
                // The 2px underline sits on the element itself rather than a
                // pseudo-element so the selected tab keeps its own height and
                // the row does not shift by a pixel as selection moves.
                className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-brand-soft ${
                  selected
                    ? 'border-brand text-fg'
                    : 'border-transparent text-fg-muted hover:text-fg'
                }`}
                style={{
                  transitionDuration: 'var(--d-fast)',
                  transitionTimingFunction: 'var(--ease-out)',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-6 py-5">
          {activeTab === 'security' && (
            <section role="tabpanel" id="settings-panel-security" aria-labelledby="settings-tab-security">
              <h3 className={SECTION_HEADING_CLASS}>
                Security parameters
              </h3>
              <SecurityParamsSection
                securityParams={securityParams || {}}
                onApply={handleApply}
                onClear={handleClear}
              />
            </section>
          )}

          {activeTab === 'memory' && (
            <section role="tabpanel" id="settings-panel-memory" aria-labelledby="settings-tab-memory">
              <h3 className={SECTION_HEADING_CLASS}>What agents remember about you</h3>
              <MemorySection />
            </section>
          )}

          {activeTab === 'theme' && (
            <section role="tabpanel" id="settings-panel-theme" aria-labelledby="settings-tab-theme">
              <h3 className={SECTION_HEADING_CLASS}>Appearance</h3>
              <ThemeSection />
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

SettingsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
